import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Design, SavedRoom } from "./types";
import { track } from "./telemetry";

const STORAGE_KEY = "room-canvas:library:v1";
const STORAGE_VERSION = 1;

function uid(prefix = "saved"): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function trimName(input: string | undefined, fallback: string): string {
  const t = (input ?? "").trim();
  return t.length > 0 ? t : fallback;
}

/**
 * Where the library is currently being persisted.
 *
 * - `local-only` — Firebase isn't configured, or the user is signed out.
 *                  Rooms only live in localStorage on this device.
 * - `syncing`    — Initial cloud reconciliation is in flight (right after
 *                  sign-in, or while applying a remote snapshot).
 * - `synced`     — Local state matches the cloud copy (best-effort —
 *                  pending writes are buffered by Firestore offline).
 * - `error`      — The most recent cloud op failed. Local changes still
 *                  persist on-device; we'll retry on the next mutation.
 */
export type CloudStatus = "local-only" | "syncing" | "synced" | "error";

interface LibraryState {
  /** All saved rooms, newest-first. */
  rooms: SavedRoom[];

  /** Current cloud-sync status. Driven by the librarySync orchestrator. */
  cloudStatus: CloudStatus;

  /**
   * Human-readable error from the most recent failed cloud op, or null.
   * Set by the sync orchestrator alongside `cloudStatus === 'error'`.
   */
  cloudError: string | null;

  /**
   * Save a fresh snapshot of `design` under `name` (or fall back to the
   * design's room name). Returns the new SavedRoom's id.
   */
  saveDesign: (design: Design, name?: string) => string;

  /**
   * Overwrite an existing saved room with a new design snapshot. Keeps the
   * original `savedAt` and `name` (unless a new `name` is provided), and
   * bumps `updatedAt`. No-ops if the id is unknown.
   */
  updateDesign: (id: string, design: Design, name?: string) => void;

  /**
   * Replace a saved room's design when a shared-room save lands for the
   * same id. No-ops if the id is unknown or the design already matches,
   * and does not emit `room_saved` — shared edits commit continuously.
   * Bumps `updatedAt` and moves the room to the top when it does write.
   */
  syncSavedDesign: (id: string, design: Design) => void;

  /** Rename a saved room. */
  renameRoom: (id: string, name: string) => void;

  /** Delete a saved room. */
  deleteRoom: (id: string) => void;

  /** Wipe the entire library. */
  clearAll: () => void;

  /**
   * Replace the entire rooms list. Used by the cloud-sync orchestrator
   * when reconciling a remote snapshot into local state. Kept separate
   * from the mutators above so the orchestrator can suppress the echo
   * write-back that would otherwise create a loop.
   */
  _setRoomsFromSync: (rooms: SavedRoom[]) => void;

  /** Cloud-sync orchestrator hook — update the surface status. */
  _setCloudStatus: (status: CloudStatus, error?: string | null) => void;
}

export const useLibrary = create<LibraryState>()(
  persist(
    (set) => ({
      rooms: [],
      cloudStatus: "local-only",
      cloudError: null,

      saveDesign: (design, name) => {
        const id = uid();
        const now = Date.now();
        const entry: SavedRoom = {
          id,
          name: trimName(name, design.room.name || "Untitled room"),
          savedAt: now,
          updatedAt: now,
          design: structuredClone(design),
        };
        set((s) => ({ rooms: [entry, ...s.rooms] }));
        track("room_saved", { id, is_new: true });
        return id;
      },

      updateDesign: (id, design, name) => {
        let updated = false;
        set((s) => {
          const idx = s.rooms.findIndex((r) => r.id === id);
          if (idx === -1) return s;
          const existing = s.rooms[idx];
          const next: SavedRoom = {
            ...existing,
            name: name !== undefined ? trimName(name, existing.name) : existing.name,
            updatedAt: Date.now(),
            design: structuredClone(design),
          };
          // Move the just-updated entry to the top of the list so the
          // most recently touched room is easy to find.
          const without = s.rooms.filter((r) => r.id !== id);
          updated = true;
          return { rooms: [next, ...without] };
        });
        if (updated) track("room_saved", { id, is_new: false });
      },

      syncSavedDesign: (id, design) => {
        set((s) => {
          const existing = s.rooms.find((r) => r.id === id);
          if (!existing) return s;
          if (JSON.stringify(existing.design) === JSON.stringify(design)) return s;
          const next: SavedRoom = {
            ...existing,
            updatedAt: Date.now(),
            design: structuredClone(design),
          };
          return { rooms: [next, ...s.rooms.filter((r) => r.id !== id)] };
        });
      },

      renameRoom: (id, name) =>
        set((s) => ({
          rooms: s.rooms.map((r) =>
            r.id === id
              ? { ...r, name: trimName(name, r.name), updatedAt: Date.now() }
              : r
          ),
        })),

      deleteRoom: (id) => {
        set((s) => ({ rooms: s.rooms.filter((r) => r.id !== id) }));
        track("room_deleted", { id });
      },

      clearAll: () => set({ rooms: [] }),

      _setRoomsFromSync: (rooms) =>
        set({ rooms: rooms.slice().sort((a, b) => b.updatedAt - a.updatedAt) }),

      _setCloudStatus: (status, error = null) =>
        set({ cloudStatus: status, cloudError: error }),
    }),
    {
      name: STORAGE_KEY,
      version: STORAGE_VERSION,
      // Persist only `rooms` to localStorage. Cloud status is recomputed
      // on every page load (it depends on the live auth state) so caching
      // a stale value would just lie to the user.
      partialize: (s) => ({ rooms: s.rooms }),
    }
  )
);
